// Order-preserving alignment of two block lists (RES-561 compare feature).
//
// 1. LCS over exact block signatures gives the unchanged anchors.
// 2. Inside each gap between anchors, remaining same-type blocks are paired as
//    'modified' when their text similarity clears the threshold; everything left
//    over is 'removed' (left only) or 'added' (right only).
//
// Reused recursively for list-items and table-cell contents.

import { blockSignature, similarity } from './signatures';

export const SIMILARITY_THRESHOLD = 0.4;

// Returns ops in document order: {kind: 'unchanged'|'modified', left, right} |
// {kind: 'removed', left} | {kind: 'added', right} — left/right are indexes into
// the input arrays.
export default function alignBlocks(leftBlocks, rightBlocks) {
	const leftSigs = leftBlocks.map(blockSignature);
	const rightSigs = rightBlocks.map(blockSignature);
	const anchors = lcsPairs(leftSigs, rightSigs);
	const ops = [];
	let li = 0;
	let ri = 0;
	const boundaries = anchors.concat([[leftBlocks.length, rightBlocks.length]]);
	boundaries.forEach(([ai, aj], idx) => {
		ops.push(...pairGap(leftBlocks, rightBlocks, li, ai, ri, aj));
		if (idx < anchors.length)
			ops.push({ kind: 'unchanged', left: ai, right: aj });
		li = ai + 1;
		ri = aj + 1;
	});
	return ops;
}

// Longest common subsequence over signature arrays; returns [leftIdx, rightIdx] pairs.
function lcsPairs(leftSigs, rightSigs) {
	const n = leftSigs.length;
	const m = rightSigs.length;
	const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
	for (let i = n - 1; i >= 0; i--)
		for (let j = m - 1; j >= 0; j--)
			dp[i][j] = leftSigs[i] === rightSigs[j]
				? dp[i + 1][j + 1] + 1
				: Math.max(dp[i + 1][j], dp[i][j + 1]);
	const pairs = [];
	let i = 0;
	let j = 0;
	while (i < n && j < m) {
		if (leftSigs[i] === rightSigs[j]) {
			pairs.push([i, j]);
			i++;
			j++;
		} else if (dp[i + 1][j] >= dp[i][j + 1]) {
			i++;
		} else {
			j++;
		}
	}
	return pairs;
}

// Pair leftover blocks between two anchors. Greedy best-match, forward-only so
// pairings never cross (both sides stay in document order).
function pairGap(leftBlocks, rightBlocks, lStart, lEnd, rStart, rEnd) {
	const rightPaired = new Array(rEnd - rStart).fill(false);
	const pairForLeft = new Map();
	let searchFrom = rStart;
	for (let i = lStart; i < lEnd; i++) {
		let best = -1;
		let bestScore = 0;
		for (let j = searchFrom; j < rEnd; j++) {
			if (rightPaired[j - rStart])
				continue;
			if ((leftBlocks[i].type || 'text') !== (rightBlocks[j].type || 'text'))
				continue;
			const score = similarity(leftBlocks[i], rightBlocks[j]);
			if (score > bestScore) {
				best = j;
				bestScore = score;
			}
		}
		if (best >= 0 && bestScore >= SIMILARITY_THRESHOLD) {
			rightPaired[best - rStart] = true;
			pairForLeft.set(i, best);
			searchFrom = best + 1;
		}
	}
	const ops = [];
	let j = rStart;
	for (let i = lStart; i < lEnd; i++) {
		if (pairForLeft.has(i)) {
			const pj = pairForLeft.get(i);
			for (; j < pj; j++)
				if (!rightPaired[j - rStart])
					ops.push({ kind: 'added', right: j });
			ops.push({ kind: 'modified', left: i, right: pj });
			j = pj + 1;
		} else {
			ops.push({ kind: 'removed', left: i });
		}
	}
	for (; j < rEnd; j++)
		if (!rightPaired[j - rStart])
			ops.push({ kind: 'added', right: j });
	return ops;
}
